import { forwardRef } from "react";
import { cn } from "../lib/cn";
import {
  CheckCircleIcon,
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  WarningIcon,
} from "../lib/icons";

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  severity?: "success" | "warning" | "error" | "info";
  variant?: "standard" | "filled" | "outlined";
  onClose?: React.EventHandler<React.MouseEvent>;
  /** Override the default leading indicator; `false` removes it entirely. */
  icon?: React.ReactNode | false;
  children: React.ReactNode;
}

type AlertSeverity = NonNullable<AlertProps["severity"]>;
type AlertVariant = NonNullable<AlertProps["variant"]>;

/** Filled carries the full status surface — the only place chips-quiet rules relax. */
const filledSeverityClasses: Record<AlertSeverity, string> = {
  success: "bg-surface-success text-on-success",
  warning: "bg-surface-warning text-on-warning",
  error: "bg-surface-error text-on-error",
  info: "bg-surface-info text-on-info",
};

/**
 * Standard: real but subtle status tint — a 10% status wash plus a soft
 * status stroke, with the severity glyph carrying the hue. Body text stays
 * content-primary for legibility in both modes.
 */
const standardSeverityClasses: Record<AlertSeverity, string> = {
  success:
    "border border-border-success-subtle bg-surface-success-subtle text-content-primary",
  warning:
    "border border-border-warning-subtle bg-surface-warning-subtle text-content-primary",
  error:
    "border border-border-error-subtle bg-surface-error-subtle text-content-primary",
  info: "border border-border-info-subtle bg-surface-info-subtle text-content-primary",
};

/** Outlined: transparent surface with a firmer status stroke. */
const outlinedSeverityClasses: Record<AlertSeverity, string> = {
  success:
    "border border-border-success-strong bg-transparent text-content-primary",
  warning:
    "border border-border-warning-strong bg-transparent text-content-primary",
  error:
    "border border-border-error-strong bg-transparent text-content-primary",
  info: "border border-border-info-strong bg-transparent text-content-primary",
};

/** Glyph colour for the quiet variants — filled inherits the on-* pair instead. */
const severityTextClasses: Record<AlertSeverity, string> = {
  success: "text-content-success",
  warning: "text-content-warning",
  error: "text-content-error",
  info: "text-content-info",
};

const severityIcons: Record<AlertSeverity, React.ElementType> = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

function SeverityGlyph({ severity }: { severity: AlertSeverity }) {
  const Icon = severityIcons[severity];
  return <Icon aria-hidden="true" className="size-5 shrink-0" />;
}

interface AlertLeadingProps {
  severity: AlertSeverity;
  variant: AlertVariant;
  icon: React.ReactNode | false;
}

function AlertLeading({ severity, variant, icon }: AlertLeadingProps) {
  if (icon === false) {
    return null;
  }
  if (icon !== undefined) {
    return <span className="flex shrink-0 items-center">{icon}</span>;
  }
  return (
    <span
      className={cn(
        "flex shrink-0 items-center",
        variant !== "filled" ? severityTextClasses[severity] : undefined,
      )}
    >
      <SeverityGlyph severity={severity} />
    </span>
  );
}

function AlertCloseButton({
  onClose,
}: {
  onClose: React.EventHandler<React.MouseEvent>;
}) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={(event) => {
        onClose(event);
      }}
      className="-mr-1.5 -mt-1 flex size-5 shrink-0 items-center justify-center rounded-md transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focused"
    >
      <CloseIcon aria-hidden="true" className="size-4" />
    </button>
  );
}

/**
 * Inline status message. `standard` tints the surface with the status hue
 * (10% wash + soft stroke); `outlined` keeps the surface transparent with a
 * firmer status stroke; `filled` takes the full status surface. The severity
 * glyph carries the hue on the quiet variants; body copy stays neutral.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      severity = "info",
      variant = "standard",
      onClose,
      icon,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const surfaceClass =
      variant === "filled"
        ? filledSeverityClasses[severity]
        : variant === "outlined"
          ? outlinedSeverityClasses[severity]
          : standardSeverityClasses[severity];

    return (
      <div
        ref={ref}
        data-slot="alert"
        data-variant={variant}
        data-severity={severity}
        role="alert"
        className={cn(
          "flex items-start gap-2.5 rounded-lg p-3.5",
          surfaceClass,
          className,
        )}
        {...props}
      >
        <AlertLeading severity={severity} variant={variant} icon={icon} />
        <div className="type-body-md min-w-0 flex-1">{children}</div>
        {onClose ? <AlertCloseButton onClose={onClose} /> : null}
      </div>
    );
  },
);
Alert.displayName = "Alert";
